import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Client-side pager for the core lists. The lists previously rendered every
// row at once — fine at 60 tenants, painful at 600. Server-side paging can
// replace the slice later without changing this component's contract.
export function usePagedRows(rows, pageSize = 50) {
  const [page, setPage] = React.useState(0);
  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pages - 1);
  React.useEffect(() => {
    // Snap back when a filter shrinks the set below the current page.
    if (page > pages - 1) setPage(0);
  }, [pages, page]);
  return {
    pageRows: rows.slice(safePage * pageSize, (safePage + 1) * pageSize),
    page: safePage,
    pages,
    total,
    setPage,
  };
}

const ListPager = ({ page, pages, total, setPage, noun = 'rows' }) => {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 bg-slate-50/50">
      <span className="text-[12px] text-slate-400">
        {total} {noun} · page {page + 1} of {pages}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
          className="p-1.5 rounded-md text-slate-500 hover:bg-white hover:text-slate-700 disabled:opacity-40"
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => setPage(Math.min(pages - 1, page + 1))}
          disabled={page >= pages - 1}
          className="p-1.5 rounded-md text-slate-500 hover:bg-white hover:text-slate-700 disabled:opacity-40"
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ListPager;
